import React from "react";
import { ArrowLeft, BookOpen, FlaskConical, Info, MessageSquare, RefreshCcw } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../app/providers/AuthProvider";
import { FK } from "../../capabilities/featureKeys";
import AiQualificationWalkthrough from "./AiQualificationWalkthrough";
import { runAiTestConsole } from "./api/aiAgentApi";
import { readAiAgentDraft, writeAiAgentDraft } from "./aiAgentDraftStorage";

const DEFAULT_ANSWER = {
  answer: "",
  knowledgeState: "",
  sourcesUsed: [],
};

const TEST_CONSOLE_DRAFT_KEY = "test-console";
const RESULT_LABELS = { SourcesFound: "Sources found", NoReadyKnowledge: "No published knowledge", NoVerifiedSourceFound: "No matching source", ProviderFailed: "Provider unavailable", DisabledOrNotAllowed: "Test not allowed" };

function stateClasses(state) {
  if (state === "SourcesFound") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (state === "ProviderFailed") return "bg-rose-50 text-rose-700 border-rose-200";
  if (state === "DisabledOrNotAllowed") return "bg-slate-100 text-slate-700 border-slate-200";
  return "bg-amber-50 text-amber-700 border-amber-200";
}

function formatCost(value) {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  return `$${number.toFixed(6)}`;
}

export default function AiTestConsolePage() {
  const auth = useAuth() || {};
  const businessId = auth.effectiveBusinessId || (auth.canSelectManagedBusinessScope ? auth.selectedBusinessId : auth.businessId) || auth.business?.id;
  const userId = auth.user?.id || auth.user?.userId;
  return <TestConsole key={`${businessId || ""}:${userId || ""}`} auth={auth} scope={{ businessId, userId }} />;
}

function TestConsole({ auth, scope }) {
  const { can, hasAllAccess } = auth;
  const [testType, setTestType] = React.useState("knowledge");
  const canRun =
    Boolean(hasAllAccess) ||
    (typeof can === "function" &&
      can(FK.AI_AGENT_ENABLED) &&
      can(FK.AI_TEST_CONSOLE_USE));

  const restoredDraft = React.useMemo(
    () => readAiAgentDraft(TEST_CONSOLE_DRAFT_KEY, scope) || {},
    [scope]
  );
  const [question, setQuestion] = React.useState(restoredDraft.question || "");
  const [topK, setTopK] = React.useState(Number(restoredDraft.topK) || 5);
  const [result, setResult] = React.useState(DEFAULT_ANSWER);
  const [submission, setSubmission] = React.useState(null);
  const [error, setError] = React.useState("");
  const requestId = React.useRef(0);
  const inFlight = React.useRef(false);
  React.useEffect(() => () => { requestId.current += 1; }, []);
  React.useEffect(() => {
    if (!canRun) { requestId.current += 1; inFlight.current = false; setLoading(false); setResult(DEFAULT_ANSWER); setSubmission(null); }
  }, [canRun]);
  const [loading, setLoading] = React.useState(false);

  const persistDraft = React.useCallback((next = {}) => {
    writeAiAgentDraft(TEST_CONSOLE_DRAFT_KEY, {
      question,
      topK,
      ...next,
    }, scope);
  }, [question, topK, scope]);

  const handleQuestionChange = value => {
    setResult(DEFAULT_ANSWER); setSubmission(null); setError("");
    setQuestion(value);
    persistDraft({ question: value });
  };

  const handleTopKChange = value => {
    setResult(DEFAULT_ANSWER); setSubmission(null); setError("");
    setTopK(value);
    persistDraft({ topK: value });
  };

  const runTest = async () => {
    if (!canRun) {
      toast.error("You do not have permission to use the AI test console.");
      return;
    }

    const trimmed = String(question || "").trim();
    if (!trimmed) {
      toast.warn("Question is required.");
      return;
    }

    if (inFlight.current) return;
    inFlight.current = true;
    const id = ++requestId.current;
    const submitted = { question: trimmed, topK: Number(topK) || 5 };
    setSubmission(submitted); setResult(DEFAULT_ANSWER); setError("");
    setLoading(true);
    try {
      const response = await runAiTestConsole(submitted);
      if (id !== requestId.current) return;
      setResult({ ...DEFAULT_ANSWER, ...response });
    } catch (error) {
      if (id !== requestId.current) return;
      const payload = error?.response?.data;
      if (payload?.knowledgeState) {
        setResult({ ...DEFAULT_ANSWER, ...payload });
      }
      const message = payload?.message || payload?.deniedReason ||
        (error?.response?.status === 429 ? "Test quota is exhausted. Review your test allowance before retrying." :
          error?.response?.status === 403 ? "You do not have permission to run this test." : "AI test failed. Check your connection and retry.");
      setError(message); toast.error(message);
    } finally {
      if (id === requestId.current) { inFlight.current = false; setLoading(false); }
    }
  };

  const sources = Array.isArray(result.sourcesUsed) ? result.sourcesUsed : [];

  const canViewSettings = Boolean(hasAllAccess) || Boolean(can?.(FK.AI_AGENT_SETTINGS_VIEW));
  const hasResult = Boolean(result.knowledgeState || result.answer);
  return (
    <div className="min-h-[calc(100vh-80px)] bg-slate-50 p-6">
      <div className="mx-auto max-w-6xl space-y-4">
        <header className="flex items-start justify-between gap-4 py-1">
          <div>
            <h1 className="flex items-center gap-2 text-lg font-semibold text-slate-900"><FlaskConical aria-hidden="true" className="h-5 w-5 text-emerald-700" />Test your assistant</h1>
            <p className="mt-1 text-sm text-slate-600">Check business answers or try your saved questions before using them with customers.</p>
          </div>
          {canViewSettings ? <Link to="/app/ai-agent/settings?section=autoreply" className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-600"><ArrowLeft aria-hidden="true" className="h-4 w-4" />Back to setup</Link> : null}
        </header>
        {!canRun ? <p role="alert" className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">Your role needs AI Agent and test console permissions to run a test.</p> : null}
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <nav aria-label="Test type" className="flex flex-wrap gap-6 border-b border-slate-200 bg-slate-50 px-5">
            {[{id: "knowledge", label: "Answer test", icon: BookOpen}, {id: "qualification", label: "Conversation walkthrough", icon: MessageSquare}].map(item => <button key={item.id} type="button" aria-pressed={testType === item.id} onClick={() => setTestType(item.id)}
              className={`inline-flex items-center gap-2 border-b-2 py-3 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-600 ${testType === item.id ? "border-emerald-600 font-medium text-slate-900" : "border-transparent text-slate-600 hover:text-slate-900"}`}><item.icon aria-hidden="true" className="h-4 w-4" />{item.label}</button>)}
          </nav>
          {testType === "qualification" ? <AiQualificationWalkthrough canRun={canRun && (Boolean(hasAllAccess) || Boolean(can?.(FK.AI_AUTO_REPLY_MANAGE)))} /> : (
          <div className="grid lg:grid-cols-[360px_minmax(0,1fr)]">
            <section aria-label="Test input" className="space-y-4 border-b border-slate-200 p-5 lg:border-b-0 lg:border-r">
              <div><h2 className="text-sm font-semibold text-slate-900">Ask a customer question</h2><p className="mt-1 text-xs text-slate-500">Uses published knowledge and your saved answer settings.</p></div>
              <label className="block text-sm text-slate-700">Test question
                <textarea value={question} onChange={event => handleQuestionChange(event.target.value)} disabled={!canRun || loading} rows={6}
                  className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none disabled:bg-slate-50" placeholder="For example, what services do you offer?" />
              </label>
              <details className="rounded-md border border-slate-200 p-3">
                <summary className="cursor-pointer text-xs text-slate-600">Advanced retrieval</summary>
                <label className="mt-3 block text-sm text-slate-700">Sources to retrieve
                  <select value={topK} onChange={event => handleTopKChange(Number(event.target.value))} disabled={!canRun || loading} className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm">
                    {[1, 3, 5, 8, 10].map(option => <option key={option} value={option}>{option}</option>)}
                  </select>
                </label>
              </details>
              <button type="button" onClick={runTest} disabled={!canRun || loading} className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-emerald-600 bg-white px-4 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50">
                {loading ? <RefreshCcw aria-hidden="true" className="h-4 w-4 animate-spin" /> : <FlaskConical aria-hidden="true" className="h-4 w-4" />}{loading ? "Running test..." : "Run Test"}
              </button>
              <p className="flex items-start gap-2 text-xs text-slate-600"><Info aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>Uses test quota and may incur AI provider cost. No customer message is sent.</span></p>
              <details className="text-xs text-slate-500"><summary className="cursor-pointer">How this test works</summary><p className="mt-2">It looks for relevant published sources first. Chat completion runs only when verified knowledge is found. Review the answer against its sources; this test does not check delivery.</p></details>
            </section>
            <section aria-label="Test result" className="min-w-0 space-y-5 p-5" aria-live="polite" aria-busy={loading}>
              <div className="flex flex-wrap items-center justify-between gap-3"><h2 className="text-sm font-semibold text-slate-900">Test result</h2>{result.knowledgeState ? <span className={`rounded-md border px-2 py-1 text-xs ${stateClasses(result.knowledgeState)}`}>{RESULT_LABELS[result.knowledgeState] || result.knowledgeState}</span> : null}</div>
              {submission ? <div className="border-b border-slate-200 pb-4 text-xs text-slate-600"><p className="break-words">{loading ? "Testing" : "Submitted question"}: {submission.question}</p><p className="mt-1">Up to {submission.topK} sources &middot; Saved configuration at execution</p></div> : null}
              {loading ? <p role="status" className="flex items-center gap-2 text-sm text-slate-600"><RefreshCcw aria-hidden="true" className="h-4 w-4 animate-spin" />Checking knowledge and preparing the result...</p> : null}
              {error ? <p role="alert" className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</p> : null}
              {!submission ? <div className="flex min-h-64 flex-col items-center justify-center gap-3 text-center text-slate-500"><FlaskConical aria-hidden="true" className="h-7 w-7 text-slate-400" /><p className="text-sm font-medium text-slate-700">Your test result will appear here</p><p className="max-w-sm text-xs">Enter a question to review the answer, matched sources and recorded AI usage together.</p></div> : null}
              {result.knowledgeState ? <p className="text-sm text-slate-600">{
                result.knowledgeState === "NoReadyKnowledge" ? "Publish a knowledge source and wait for Ready before testing." :
                result.knowledgeState === "NoVerifiedSourceFound" ? "No relevant published source matched. Add the missing information or try a more specific question." :
                result.errorCode?.toLowerCase().includes("configured") ? "Ask an administrator to check provider and embedding configuration." :
                result.knowledgeState === "ProviderFailed" ? "The AI or embedding provider failed. Check configuration and availability, then retry." :
                result.knowledgeState === "DisabledOrNotAllowed" ? "Check saved AI enablement and test permissions." : "Review this answer against the recorded sources. Delivery is not tested."
              }</p> : null}
              {result.answer ? <div><h3 className="mb-2 text-xs font-medium text-slate-500">Answer preview</h3><p className="whitespace-pre-wrap break-words rounded-md bg-slate-50 p-4 text-sm text-slate-800">{result.answer}</p></div> : submission && !loading ? <p className="text-sm text-slate-500">No answer was produced for this test.</p> : null}
              {hasResult ? <>
                <section className="border-t border-slate-200 pt-4"><h3 className="text-sm font-medium text-slate-900">Sources used <span className="font-normal text-slate-500">({sources.length})</span></h3>
                  {!sources.length ? <p className="mt-2 text-sm text-slate-500">No verified source was recorded for this result.</p> : <div className="mt-3 divide-y divide-slate-200">{sources.map(source => <details key={source.chunkId || `${source.knowledgeSourceId}-${source.chunkIndex}`} className="py-3 first:pt-0">
                    <summary className="cursor-pointer text-sm font-medium text-slate-800">{source.title}</summary><p className="mt-2 whitespace-pre-wrap break-words text-sm text-slate-700">{source.excerpt}</p><p className="mt-2 text-xs text-slate-500">{source.sourceType} &middot; Excerpt {Number(source.chunkIndex) + 1}{source.similarityScore !== undefined ? ` / Similarity score ${Number(source.similarityScore).toFixed(3)}` : ""}</p>
                  </details>)}</div>}
                </section>
                <details className="border-t border-slate-200 pt-4"><summary className="cursor-pointer text-sm text-slate-600">Usage and diagnostics</summary>
                  <dl className="mt-3 grid grid-cols-2 gap-3 rounded-md bg-slate-50 p-3 text-sm">{[["Provider", result.provider || "Not recorded"], ["Model", result.model || "Not recorded"], ["Tokens", result.totalTokens ?? "Not recorded"], ["Estimated AI cost (USD)", formatCost(result.estimatedCostUsd)]].map(([label,value]) => <div key={label}><dt className="text-xs text-slate-500">{label}</dt><dd className="mt-1 break-words text-slate-800">{value}</dd></div>)}</dl>
                  {result.errorCode || result.deniedReason ? <p className="mt-3 break-words text-xs text-rose-700">{result.deniedReason || result.errorCode}</p> : null}
                  <p className="mt-2 text-xs text-slate-500">Recorded estimates are not a customer bill. Similarity scores describe retrieval matches, not answer accuracy.</p>
                </details>
              </> : null}
            </section>
          </div>)}
        </div>
      </div>
    </div>
  );
}
